import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { VoiceConfirmRow } from "@/components/add/VoiceConfirmRow";
import { AccountHintBanner } from "@/components/add/AccountHintBanner";

export function VoiceConfirmModal({ open, transcript, extracted, onClose, onSaved }) {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [ccys, setCcys] = useState([]);
  const [rates, setRates] = useState({});
  const [userCcy, setUserCcy] = useState("AED");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const today = new Date().toLocaleDateString("en-CA");
    setItems((extracted || []).map((it) => ({ ...it, date: it.date || today })));
  }, [extracted]);
  useEffect(() => {
    if (!open) return;
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/currencies").then((r) => setCcys(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => {
      const cur = r.data?.currency || "AED";
      setUserCcy(cur);
      api.get(`/fx?base=${cur}`).then((rr) => setRates(rr.data?.rates || {})).catch(() => {});
    }).catch(() => {});
  }, [open]);

  if (!open) return null;

  const patch = (i, next) => setItems(items.map((v, k) => (k === i ? next : v)));
  const remove = (i) => setItems(items.filter((_, k) => k !== i));

  const save = async () => {
    if (items.length === 0) { onClose(); return; }
    setBusy(true);
    try {
      const saved = await Promise.all(items.map((it) =>
        api.post("/transactions", {
          type: it.type, amount: it.amount, currency: it.currency,
          category: it.category, description: it.description || "",
          date: it.date, account_id: it.account_id || null, to_account_id: it.to_account_id || null, force: true,
        }).then((r) => r.data)
      ));
      toast.success(t("added").replace("{n}", saved.length));
      onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-md grid place-items-center p-3">
      <div className="w-full max-w-lg neu rounded-3xl flex flex-col max-h-[92vh]">
        <div className="p-6 pb-3 shrink-0">
          <p className="text-xs uppercase tracking-widest text-slate-500">{lang === "ar" ? "تأكيد الحفظ" : "Confirm save"}</p>
          <p className="text-sm mt-1 text-slate-600 italic line-clamp-2">"{transcript}"</p>
          <p className="text-xs text-slate-400 mt-1">{items.length} {lang === "ar" ? "معاملة" : "items"}</p>
        </div>
        <div className="flex-1 overflow-auto px-6 space-y-3">
          {items.length === 0 && <p className="text-center text-sm text-slate-500 py-6">{lang === "ar" ? "لا شيء للحفظ" : "Nothing to save"}</p>}
          <AccountHintBanner items={items} onItemsUpdate={setItems} />
          {items.map((it, i) => (
            <VoiceConfirmRow key={i} index={i} row={it} cats={cats} ccys={ccys}
              userCcy={userCcy} rates={rates}
              onChange={(next) => patch(i, next)} onRemove={() => remove(i)} />
          ))}
        </div>
        <div className="p-6 pt-3 flex gap-2 shrink-0">
          <button data-testid="voice-cancel-btn" onClick={onClose} disabled={busy}
            className="flex-1 py-3 rounded-2xl neu-sm neu-accent-blue font-semibold flex items-center justify-center gap-2">
            <X className="w-4 h-4" /> {lang === "ar" ? "إلغاء" : "Cancel"}
          </button>
          <button data-testid="voice-confirm-btn" onClick={save}
            disabled={busy || items.length === 0 || items.some((it) => !it.account_id)}
            className="flex-1 py-3 rounded-2xl neu-fill-blue text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-blue-900/30 disabled:opacity-40 disabled:cursor-not-allowed">
            <Check className="w-4 h-4" /> {lang === "ar" ? "حفظ" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
